<template>
  <div class="flex">
    <div
      :class="isOpen ? 'block' : 'hidden'"
      @click="clickOpen"
      class="
        fixed
        inset-0
        z-20
        transition-opacity
        bg-black
        opacity-50
        lg:hidden
      "
    ></div>
    <aside
      :class="isOpen ? 'translate-x-0 ease-out' : '-translate-x-full ease-in'"
      class="fixed inset-y-0 left-0 z-30 w-64 overflow-y-auto transition duration-300 transform bg-gray-900 lg:translate-x-0 lg:static lg:inset-0"
    >
      <div class="mt-8 text-2xl px-5 font-semibold text-white">
        <span>Admin</span>
        <div v-if="isNotProduction" class="text-rose-600 text-xl">
          개발 테스트 페이지
        </div>
      </div>
      <nav class="mt-10 min-h-[80vh] overflow-auto">
        <div
          class="px-4"
          v-for="(sideBar, index) in sideBars" :key="index"
        >
          <div v-if="showSidebarItem(sideBar)">
            <router-link
              v-if="sideBar.type === 'own'"
              :to="sideBar.path"
              :class="[
                'flex items-center py-2 duration-200 border-l-4 text-decoration-none',
                $route.name === sideBar.routeName ? activeClass : inactiveClass,
                sideBar.isSuperAdmin && !isSuperAdmin ? 'hidden' : ''
              ]"
              @click="handleNavClick(sideBar)"
            >
              <span class="mx-4">{{ sideBar.name }}</span>
            </router-link>
            <div v-else>
              <div
                @click="openSideBar(sideBar.routeName)"
                :class="[
                  'flex items-center py-2 duration-200 border-l-4 text-decoration-none border-gray-900 text-gray-500 hover:bg-gray-600 hover:bg-opacity-25 hover:text-gray-100',
                  // @ts-ignore
                  sideBar.childrenRouteNames.includes($route.name) ? activeClass : inactiveClass,
                  sideBar.isSuperAdmin && !isSuperAdmin ? 'hidden' : ''
                ]"
              >
                <span class="mx-4">{{ sideBar.name }}</span>
              </div>
              <router-link
                v-for="(children, index) in sideBar.children"
                :key="index"
                :to="children.path"
                :class="[
                  store.state.navigation.openSideBarNames.includes(sideBar.routeName) ? '' : 'hidden',
                  'flex items-center py-2 duration-200 border-l-4 text-decoration-none',
                  $route.name === children.routeName ? activeClass : inactiveClass
                ]"
                @click="handleNavClick(children)"
              >
                <span class="ml-10">{{ children.name }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </nav>
      <div class="mx-4 mt-5">
        <router-link
          to="/login"
          class="px-6 py-2 mt-2 duration-200 border-l-4 text-decoration-none"
          :class="[inactiveClass]"
          @click="logout"
        >
          Log out
        </router-link>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";

const store = useStore()
const route = useRoute()
const router = useRouter()
const isSuperAdmin = localStorage.getItem("IS_SUPER_ADMIN") === "true"

const isNotProduction = process.env.VUE_APP_ENV !== 'production'

const openSideBar = (name) => {
  store.commit("navigation/setOpenSideBarNames", name)
}

const showSidebarItem = (sideBar) => {
  return (sideBar.isSuperAdmin === isSuperAdmin) || !sideBar.isSuperAdmin;
}

const logout = () => {
  // Vuex store 초기화
  store.commit("token/setToken", "")
  store.commit("token/setRefreshToken", "")
  store.commit("token/setIsSuperAdmin", false)
  store.commit("token/setRole", "")
  
  // localStorage 초기화
  localStorage.removeItem('ACCESS_TOKEN')
  localStorage.removeItem('REFRESH_TOKEN')
  localStorage.removeItem('IS_SUPER_ADMIN')
  localStorage.removeItem('ROLE')
}

// 네비게이션 클릭 핸들러 (새로고침 기능 추가)
const handleNavClick = (navItem) => {
  // 현재 페이지와 같은 페이지를 클릭한 경우
  if (route.name === navItem.routeName) {
    // 새로고침
    window.location.reload()
  } else {
    // 다른 페이지로 이동 (router-link의 기본 동작)
    // 이 경우는 router-link가 자동으로 처리
  }

  // 모바일에서 사이드바 닫기
  clickOpen()
}

const isOpen = computed(() => store.state.sideBar.isOpen)

const clickOpen = () => store.commit("sideBar/useSideBar")

const activeClass = ref(
  "bg-gray-600 bg-opacity-25 text-gray-100 border-gray-100"
);
const inactiveClass = ref(
  "border-gray-900 text-gray-500 hover:bg-gray-600 hover:bg-opacity-25 hover:text-gray-100"
)

const sideBars = [
  {
    type: "own",
    path: "/",
    routeName: "Dashboard",
    name: "상담신청 내역",
    childrenRouteNames: [],
    children: [],
    isSuperAdmin: false
  },
  {
    type: "children",
    path: "",
    routeName: "ManagePayment",
    name: "결제 관리",
    childrenRouteNames: [
      "Payments",
      "BasicAccessCodePaymentAudit",
      "AdvancedAccessCodePaymentAudit",
      "TeamLeaderAccessCodes",
    ],
    isSuperAdmin: true,
    children: [
      {
        path: "/payments",
        routeName: "Payments",
        name: "결제 내역",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/access-code-payment/basic",
        routeName: "BasicAccessCodePaymentAudit",
        name: "기초반 결제 확인",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/access-code-payment/advanced",
        routeName: "AdvancedAccessCodePaymentAudit",
        name: "실전반 결제 확인",
        children: [],
        isSuperAdmin: true,
      },
    ],
  },
  {
    type: "own",
    path: "/learning-session",
    routeName: "LearningSessions",
    name: "학습 관리",
    childrenRouteNames: [],
    children: [],
    isSuperAdmin: true
  },
  {
    type: "own",
    path: "/notices",
    routeName: "Notices",
    name: "공지사항",
    childrenRouteNames: [],
    children: [],
    isSuperAdmin: true
  },
  {
    type: "own",
    path: "/lesson-accesses",
    routeName: "LessonAccesses",
    name: "학생 코드 관리",
    childrenRouteNames: [],
    isSuperAdmin: true,
    children: [],
  },
  {
    type: "own",
    path: "/advance-lesson-accesses",
    routeName: "AdvancedClassLessonAccesses",
    name: "실전반 학생 코드 관리",
    childrenRouteNames: [],
    isSuperAdmin: true,
    children: [],
  },
  {
    type: "own",
    path: "/team-leader-access-codes",
    routeName: "TeamLeaderAccessCodes",
    name: "실전반 팀장 접속 코드",
    children: [],
    isSuperAdmin: true,
  },
  {
    type: "own",
    path: "/lesson_content_unlock_codes",
    routeName: "LessonContentUnlockCodes",
    name: "강의 접속코드 관리",
    childrenRouteNames: [],
    isSuperAdmin: true,
  },
  {
    type: "children",
    path: "",
    routeName: "ManageLesson",
    name: "강의 관리",
    childrenRouteNames: [
      "Files",
      "LessonPopups"
    ],
    isSuperAdmin: true,
    children: [
      {
        path: "/files",
        routeName: "Files",
        name: "교재 관리",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/popup",
        routeName: "LessonPopups",
        name: "팝업관리",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/notice-modal",
        routeName: "NoticeModals",
        name: "중단 팝업",
        children: [],
        isSuperAdmin: true,
      },
    ],
  },
  {
    type: "children",
    path: "",
    routeName: "ManageLessonProduct",
    name: "결제 상품 관리",
    childrenRouteNames: [
      "LessonProducts",
      "LessonProductDiscounts",
      "LessonProductOptions",
      "PaymentLinks",
      "Products"
    ],
    isSuperAdmin: true,
    children: [
      {
        path: "/lesson_products",
        routeName: "LessonProducts",
        name: "결제 상품",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/lesson_product_discounts",
        routeName: "LessonProductDiscounts",
        name: "결제 할인",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/lesson_product_options",
        routeName: "LessonProductOptions",
        name: "결제 옵션",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/offline-lessons",
        routeName: "OfflineLessons",
        name: "오프라인 수강 신청",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/payment-notices",
        routeName: "PaymentNotices",
        name: "안내사항",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/payment-links",
        routeName: "PaymentLinks",
        name: "(구) 결제 링크",
        children: [],
        isSuperAdmin: true,
      },
      {
        path: "/products",
        routeName: "Products",
        name: "(구) 결제 상품",
        children: [],
        isSuperAdmin: true,
      },
    ],
  },
]

</script>
<style>
.log-out {
  position: absolute;
  bottom: 1rem;
}

.text-decoration-none {
  text-decoration: none;
}
</style>